/**
 * The editable-prop schema behind the block properties panel.
 *
 * The panel is generated from this description rather than hand-wired per tag,
 * so adding a primitive to the vocabulary means adding one entry to
 * `TAG_PROPS` here. The container-prop half is derived from the shared
 * `FP_CONTAINER_ATTRIBUTES`, so a new container attribute appears on every
 * primitive that accepts it without touching this file at all.
 *
 * A prop that needs a control the generator cannot express - a field-source
 * picker, an asset uploader - opts out through `FP_PROP_OVERRIDES` and is
 * rendered by hand in `BlockSettingsPopover`.
 */

import {
  FP_CONTAINER_ATTRIBUTES,
  FP_LINE_FIELD_SOURCES,
  FP_PAGE_NUMBER_FORMAT_ATTRIBUTE,
  FP_PAGE_NUMBER_FORMATS,
  FP_TABLE_REPEAT_HEADER_ATTRIBUTE,
  FP_TAG_ATTRIBUTES,
  type FpPageNumberFormat,
} from "@forgepoint/shared";
import { parseDocumentHtml, resolveBlock } from "./documentEditor";
import { TEXT_ALIGN_OPTIONS } from "./styleOptions";

/** How the panel renders one prop's control. */
export type FpPropControl =
  | "length"
  | "margin"
  | "color"
  | "select"
  | "text"
  | "toggle";

/** Whether a prop describes what the block binds or how its box looks. */
export type FpPropGroup = "content" | "container";

/** One selectable value for a `select` prop. */
export interface FpPropOption {
  /** The attribute value written when the option is picked. */
  value: string;
  /** The option's panel label. */
  label: string;
}

/** One editable prop on an fp-* tag. */
export interface FpPropDefinition {
  /** The attribute name written on the element. */
  name: string;
  /** The prop's panel label. */
  label: string;
  /** How the panel renders the control. */
  control: FpPropControl;
  /** Which section of the panel the prop belongs to. */
  group: FpPropGroup;
  /** The values a `select` offers, empty for every other control. */
  options: readonly FpPropOption[];
  /** The value a `toggle` writes when switched on, or null. */
  checkedValue: string | null;
  /** Placeholder copy hinting the expected syntax, or null. */
  placeholder: string | null;
  /** What the renderer falls back to when the prop is unset, or null. */
  default: string | null;
}

/**
 * Props rendered by a bespoke control instead of the generated one, keyed
 * `<tag>.<prop>`. This is the schema's escape valve: the prop stays declared,
 * so the panel still knows it exists, but the control comes from elsewhere.
 */
export const FP_PROP_OVERRIDES: ReadonlySet<string> = new Set<string>([
  "fp-field.source",
  "fp-markdown.source",
  "fp-text.source",
  "fp-column.source",
  "fp-image.asset-id",
  "fp-image.width",
  "fp-image.height",
  "fp-table.number-source",
  "fp-lines.number-source",
]);

/**
 * Whether a prop opts out of the generated control.
 * @param tag - The lowercased fp-* tag name
 * @param name - The prop's attribute name
 * @returns True when the panel renders the prop by hand
 */
export function isOverriddenFpProp(tag: string, name: string): boolean {
  return FP_PROP_OVERRIDES.has(`${tag}.${name}`);
}

/**
 * Every prop declared for a tag: its own props first, then the shared container
 * props when the tag accepts them. Props the shared allowlist would reject are
 * dropped, so the panel can never author markup the sanitizer strips.
 * @param tag - The lowercased tag name
 * @returns The prop definitions, empty for a tag with no schema entry
 */
export function fpPropsForTag(tag: string): readonly FpPropDefinition[] {
  const allowed = TAG_ATTRIBUTES.get(tag);
  if (allowed === undefined) {
    return [];
  }
  const own = TAG_PROPS[tag] ?? [];
  const container = acceptsContainerProps(allowed) ? FP_CONTAINER_PROPS : [];
  return [...own, ...container].filter((prop) => allowed.has(prop.name));
}

/**
 * The props the panel generates a control for: everything declared for the tag
 * minus the overrides and minus the shared container props the toolbar owns. A
 * prop the tag declares itself (a page frame's border) stays, since the toolbar
 * only authors the shared container set.
 * @param tag - The lowercased tag name
 * @returns The prop definitions the generator owns
 */
export function generatedFpProps(tag: string): readonly FpPropDefinition[] {
  const own = new Set((TAG_PROPS[tag] ?? []).map((prop) => prop.name));
  return fpPropsForTag(tag).filter(
    (prop) =>
      !isOverriddenFpProp(tag, prop.name) &&
      !(isToolbarOwnedProp(prop.name) && !own.has(prop.name)) &&
      !(prop.name === "margin" && MARGINLESS_TAGS.has(tag)),
  );
}

/**
 * Tags that always render as part of a table - a `<tr>` or a `<td>` - where
 * CSS margin does nothing at all. The prop
 * stays declared, so a value authored before this still reads back and still
 * saves; only its dead panel control is gone. `fp-cell` is absent because it is
 * a table cell only inside a table, which the panel decides per selection.
 */
const MARGINLESS_TAGS: ReadonlySet<string> = new Set<string>([
  "fp-row",
  "fp-column",
  "fp-section-heading",
  "fp-section-notes",
]);

/** The shared per-tag attribute allowlist, as a map so a miss reads as absent. */
const TAG_ATTRIBUTES: ReadonlyMap<string, ReadonlySet<string>> = new Map(
  Object.entries(FP_TAG_ATTRIBUTES),
);

/**
 * Reads the current value of every declared prop off a block in the stored
 * document, so the panel shows what is authored rather than what was defaulted.
 * A table row's `background` reads the value its cells share - the cells are
 * the row's paint layer, and writing the row fans out to them the same way.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the block
 * @returns The authored values by attribute name, "" for an unset prop
 */
export function readFpPropValues(
  html: string,
  nodeId: string,
): Record<string, string> {
  const block = resolveBlock(parseDocumentHtml(html), nodeId);
  if (block === null) {
    return {};
  }
  const values = fpPropsForTag(block.tagName.toLowerCase()).reduce<
    Record<string, string>
  >(
    (all, prop) => ({
      ...all,
      [prop.name]: block.getAttribute(prop.name) ?? "",
    }),
    {},
  );
  if (block.tagName.toLowerCase() === "fp-row" && values.background === "") {
    const cellValues = Array.from(block.children)
      .filter((child) => child.tagName.toLowerCase() === "fp-cell")
      .map((cell) => cell.getAttribute("background") ?? "");
    const [first = ""] = cellValues;
    if (first !== "" && cellValues.every((value) => value === first)) {
      values.background = first;
    }
  }
  return values;
}

/** The parts of a prop definition a declaration may override. */
interface FpPropRefinement {
  label?: string;
  control?: FpPropControl;
  options?: readonly FpPropOption[];
  checkedValue?: string;
  placeholder?: string;
  default?: string;
}

/**
 * Fills a prop declaration out to a full definition, so a declaration only
 * states what differs from a plain free-text field.
 * @param name - The attribute name
 * @param group - Which panel section the prop belongs to
 * @param refinement - The parts of the definition the declaration states
 * @returns The complete prop definition
 */
function defineProp(
  name: string,
  group: FpPropGroup,
  refinement: FpPropRefinement = {},
): FpPropDefinition {
  return {
    name,
    label: refinement.label ?? humanizeAttribute(name),
    control: refinement.control ?? "text",
    group,
    options: refinement.options ?? [],
    checkedValue: refinement.checkedValue ?? null,
    placeholder: refinement.placeholder ?? null,
    default: refinement.default ?? null,
  };
}

/**
 * Turns an attribute name into a panel label ("min-height" -> "Min height").
 * @param name - The attribute name
 * @returns The human-readable label
 */
function humanizeAttribute(name: string): string {
  const words = name.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Turns a list of raw attribute values into picker options.
 * @param values - The attribute values, in the order they should be offered
 * @returns One option per value, labelled from the value
 */
function toOptions(values: readonly string[]): readonly FpPropOption[] {
  return values.map((value) => ({ value, label: humanizeAttribute(value) }));
}

/**
 * Whether an allowlist carries the full container-attribute set, which is what
 * `withContainerAttributes` in the shared vocabulary produces.
 * @param allowed - The tag's attribute allowlist
 * @returns True when every container attribute is present
 */
function acceptsContainerProps(allowed: ReadonlySet<string>): boolean {
  return FP_CONTAINER_ATTRIBUTES.every((name) => allowed.has(name));
}

/** Control refinements per container attribute; anything unlisted is free text. */
const CONTAINER_REFINEMENTS: Record<string, FpPropRefinement | undefined> = {
  padding: { control: "length", placeholder: "12px" },
  margin: { control: "margin", placeholder: "12px 0 0 0" },
  background: { control: "color", placeholder: "#f5f7fa" },
  border: { control: "text", placeholder: "1px solid #d1d5db" },
  "border-top": { control: "text", placeholder: "1px solid #d1d5db" },
  "border-right": { control: "text", placeholder: "1px solid #d1d5db" },
  "border-bottom": { control: "text", placeholder: "1px solid #d1d5db" },
  "border-left": { control: "text", placeholder: "1px solid #d1d5db" },
  radius: { label: "Corner radius", control: "length", placeholder: "4px" },
  width: { control: "length", placeholder: "auto" },
  align: {
    label: "Align",
    control: "select",
    options: toOptions(TEXT_ALIGN_OPTIONS),
  },
  gap: { control: "length", placeholder: "8px" },
  size: { label: "Font size", control: "length", placeholder: "12px" },
  color: { label: "Text color", control: "color", placeholder: "#111827" },
  font: {
    label: "Typeface",
    control: "text",
    placeholder: "Inter, system-ui, sans-serif",
  },
  height: { control: "length", placeholder: "auto" },
  "min-height": { control: "length", placeholder: "auto" },
  "max-height": { control: "length", placeholder: "auto" },
  "min-width": { control: "length", placeholder: "auto" },
  "max-width": { control: "length", placeholder: "auto" },
};

/**
 * The shared container props the panel still authors. Everything else the
 * vocabulary accepts - typography, colour, borders, padding, alignment - is
 * authored from the toolbar, so the panel is the block's box and its fill.
 * `background` stays here because no toolbar control authors it.
 */
const PANEL_CONTAINER_PROPS: ReadonlySet<string> = new Set<string>([
  "background",
  "margin",
  "min-height",
  "max-height",
  "min-width",
  "max-width",
]);

/** The container attribute names, as a set for membership tests. */
const CONTAINER_ATTRIBUTE_NAMES: ReadonlySet<string> = new Set<string>(
  FP_CONTAINER_ATTRIBUTES,
);

/**
 * Whether a prop is a shared container attribute the panel no longer shows.
 * The prop stays declared, so an authored value still renders and still reads
 * back; only its panel control is gone.
 * @param name - The prop's attribute name
 * @returns True when the toolbar owns the prop instead of the panel
 */
function isToolbarOwnedProp(name: string): boolean {
  return (
    CONTAINER_ATTRIBUTE_NAMES.has(name) && !PANEL_CONTAINER_PROPS.has(name)
  );
}

/**
 * The shared container props, in the shared vocabulary's order. Derived rather
 * than listed, so a new container attribute reaches every panel for free - as a
 * free-text field until it earns a refinement above.
 */
export const FP_CONTAINER_PROPS: readonly FpPropDefinition[] =
  FP_CONTAINER_ATTRIBUTES.map((name) =>
    defineProp(name, "container", CONTAINER_REFINEMENTS[name]),
  );

/** The line-scope sources an `<fp-column>` may bind. */
const LINE_SOURCE_OPTIONS = toOptions(FP_LINE_FIELD_SOURCES);

/**
 * Panel label per page-number format, spelling the shape out with `n` for the
 * page and `m` for the total rather than a sample page: the choice between the
 * formats is typographic, so the label has to show the slash and the wording,
 * and standing numbers in would read as a value rather than a shape. `Page`
 * keeps its capital because that one is literal - the format prints it. Keyed
 * by the shared format union, so a format added to the vocabulary fails to
 * compile until it is labelled here.
 */
const PAGE_NUMBER_FORMAT_LABELS: Record<FpPageNumberFormat, string> = {
  n: "n",
  "n-of-total": "n/m",
  "page-n-of-total": "Page n of m",
};

/** The page-number formats as picker options, in the shared vocabulary's order. */
const PAGE_NUMBER_FORMAT_OPTIONS: readonly FpPropOption[] =
  FP_PAGE_NUMBER_FORMATS.map((value) => ({
    value,
    label: PAGE_NUMBER_FORMAT_LABELS[value],
  }));

/**
 * The line custom field driving item numbers, declared identically on both
 * repeaters that number lines so either one can be selected and configured.
 */
const NUMBER_SOURCE_PROP = defineProp("number-source", "content", {
  label: "Item numbering",
});

/**
 * The page counters' only box prop. They sit outside the shared container set,
 * so the prop is declared per tag rather than derived.
 */
const COUNTER_MARGIN_PROP = defineProp("margin", "container", {
  control: "margin",
});

/** Tag-specific props, on top of whatever container props the tag accepts. */
const TAG_PROPS: Record<string, readonly FpPropDefinition[] | undefined> = {
  "fp-column": [
    defineProp("source", "content", {
      label: "Line value",
      control: "select",
      options: LINE_SOURCE_OPTIONS,
    }),
    defineProp("header", "content", {
      label: "Header",
      placeholder: "Price|$ stacks two lines",
    }),
    defineProp("label", "content", { placeholder: "Weight" }),
    defineProp("prefix", "content", { placeholder: "$" }),
    defineProp("suffix", "content", { placeholder: " lb" }),
  ],
  "fp-columns": [
    defineProp("widths", "container", {
      label: "Column widths",
      placeholder: "40%, 60%",
    }),
  ],
  "fp-text": [defineProp("source", "content", { label: "Field source" })],
  "fp-markdown": [defineProp("source", "content", { label: "Field source" })],
  "fp-image": [defineProp("asset-id", "content", { label: "Image" })],
  "fp-page-number": [
    defineProp(FP_PAGE_NUMBER_FORMAT_ATTRIBUTE, "content", {
      label: "Counter format",
      control: "select",
      options: PAGE_NUMBER_FORMAT_OPTIONS,
    }),
    COUNTER_MARGIN_PROP,
  ],
  "fp-page-count": [COUNTER_MARGIN_PROP],
  "fp-page-frame": [
    defineProp("border", "container", { placeholder: "1px solid #111827" }),
    defineProp("margin", "container", {
      label: "Inset from page edge",
      control: "margin",
      placeholder: "24px",
    }),
    defineProp("radius", "container", CONTAINER_REFINEMENTS.radius),
  ],
  "fp-field": [
    defineProp("source", "content", { label: "Field source" }),
    defineProp("hide-empty", "content", {
      label: "Hide when the field is empty",
      control: "toggle",
      checkedValue: "true",
    }),
  ],
  "fp-line-field": [
    defineProp("source", "content", {
      label: "Line value",
      control: "select",
      options: LINE_SOURCE_OPTIONS,
    }),
    defineProp("hide-empty", "content", {
      label: "Hide when the field is empty",
      control: "toggle",
      checkedValue: "true",
    }),
  ],
  "fp-table": [
    NUMBER_SOURCE_PROP,
    defineProp(FP_TABLE_REPEAT_HEADER_ATTRIBUTE, "content", {
      label: "Repeat the header on every page",
      control: "toggle",
      checkedValue: "true",
    }),
  ],
  "fp-lines": [NUMBER_SOURCE_PROP],
};
